<template>
  <component-wrapper :class="['retailer-search-fi', theme ? `retailer-search-fi--${theme}` : null]">
    <template v-if="isMounted">
      <div
        v-if="userLocationError"
        class="retailer-search-fi__user-location-error"
        :class="[
          theme
            ? 'fi-col-span-full'
            : 'fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12',
        ]"
      >
        <block-notification-fi
          close-btn
          type="error"
          icon="alert-triangle"
          class="fi-mb-30"
        >
          {{ $dict.get('dealer-Locator.User-Location.Error') }}
        </block-notification-fi>
      </div>

      <div class="retailer-search-fi__map">
        <loading-indicator-wrapper
          :loading="directions.loading"
          variant="spinnerLocal"
          spinoff="in-relative-centered"
        >
          <GMapMap
            ref="gmap"
            :center="{ lat: googleMaps.lat, lng: googleMaps.lng }"
            :zoom="googleMaps.zoom"
            :options="googleMaps.googleMapsOptions"
            @zoom_changed="changeZoom"
          >
            <GMapCluster
              ref="cluster"
              :zoom-on-click="true"
              :grid-size="120"
              :max-zoom="15"
              :minimum-cluster-size="2"
              :renderer="renderer"
            >
              <GMapMarker
                v-for="(marker, index) in directions.markers"
                :key="index"
                :draggable="marker.draggable"
                :position="marker.position"
                :icon="getMarkerIcon(marker.icon, false)"
                :title="marker.name"
                @dragend="markerDragged"
              />

              <GMapMarker
                v-if="(!directions.active || locationError) && activeRetailer"
                :key="activeRetailer.Retailer.Names[0]"
                :clickable="true"
                :draggable="false"
                :position="getMarkerPosition(activeRetailer)"
                :icon="getMarkerIcon(activeRetailer, true)"
                :title="activeRetailer.Retailer.Names[0]"
                :show-stock="haveArticles"
                @click="activateMarker(activeRetailer)"
              />

              <template
                v-else-if="
                  (!directions.active || locationError) && googleMaps.retailers?.length < 1000
                "
              >
                <GMapMarker
                  v-for="(retailer, index) in googleMaps.retailers"
                  :key="`${index}-${retailer.Retailer.Names[0]}`"
                  :clickable="true"
                  :draggable="false"
                  :position="getMarkerPosition(retailer)"
                  :icon="getMarkerIcon(retailer, true)"
                  :title="retailer.Retailer.Names?.[0]"
                  :show-stock="haveArticles"
                  @click="activateMarker(retailer)"
                />
              </template>
            </GMapCluster>
            <google-map-fetcher-fi @map="google = $event" />
          </GMapMap>
        </loading-indicator-wrapper>

        <div
          :class="{
            'retailer-search-fi__actions': true,
            'retailer-search-fi__actions--navigation-active': directions.active,
          }"
        >
          <retailer-search-filter-fi
            class="retailer-search-fi__filters"
            :google="google"
            :location="location"
            :location-error="locationError"
            :retailer-name="retailerName"
            :autocomplete-options="googleMaps.autoCompleteOptions"
            :ranges="retailerRanges"
            :default-range="retailerDefaultRange"
            :theme="theme"
            :retailer-search-page="retailerSearchPage"
            :retailer-types="retailerTypes"
            :retailers-count="googleMaps.retailersCount"
            :navigation-active="directions.active"
            @location-changed="locationChanged"
            @retailer-name-changed="retailerNameChanged"
            @range-changed="setRange"
            @user-location-error="userLocationErrorChanged"
            @type-changed="retailerTypeChanged"
          />

          <div class="retailer-search-fi__directions">
            <div class="retailer-search-fi__directions__wrapper">
              <div
                v-if="routeData"
                class="retailer-search-fi__directions__info"
              >
                <span
                  v-if="routeData.distance?.text"
                  class="retailer-search-fi__directions__info__distance"
                >
                  <icon-fi
                    class="retailer-search-fi__directions__icon"
                    icon="car"
                  />
                  {{ routeData.distance.text }}
                </span>
                <span
                  v-if="routeData.duration?.text"
                  class="retailer-search-fi__directions__info__duration"
                >
                  {{ routeData.duration.text }}
                </span>
              </div>

              <div
                class="retailer-search-fi__directions__close"
                @click="closeDirections"
              >
                <icon-fi icon="close" />
              </div>
            </div>

            <div
              class="retailer-search-fi__directions__maps"
              @click="openGoogleMaps"
            >
              <icon-fi
                icon="google-map-pin"
                class="retailer-search-fi__directions__icon"
              />
              {{ $dict.get('dealer-Locator.Open-In-Google-Maps') }}
            </div>
          </div>
        </div>

        <transition
          mode="in-out"
          appear
          :enter-active-class="`animated ${isSM ? 'fadeInFromRight' : 'slideInFromBottom'} faster`"
          :leave-active-class="`animated ${isSM ? 'fadeOutToRight' : 'slideOutToBottom'} faster`"
        >
          <div
            v-if="googleMaps.infoWindowOpened"
            :class="{
              'retailer-search-fi__map__info-window': true,
              'retailer-search-fi__map__info-window--partner':
                googleMaps.marker.Retailer.PartnerType,
            }"
          >
            <div class="retailer-search-fi__map__info-window__close">
              <icon-fi
                icon="close"
                @click="closeInfoWindow()"
              />
            </div>

            <div class="retailer-search-fi__map__info-window__inner">
              <retailer-detail-fi
                :retailer="googleMaps.marker.Retailer"
                :is-stocked="googleMaps.marker.IsStocked"
                :distance="googleMaps.marker.Distance"
                :unit="googleMaps.marker.Unit"
                :show-stock="haveArticles"
                @navigate-to="navigateTo"
              />

              <div
                v-if="googleMaps.marker.Retailer.PartnerType"
                class="retailer-search-fi__map__info-window__partner"
              >
                <cloud-image-fi
                  :field="fields.partnerTypeLogo"
                  class="retailer-search-fi__map__info-window__partner__img"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="isEditMode"
        class="retailer-search-fi__disclaimer"
        v-html="fields.disclaimer.editable"
      />
      <div
        v-if="!isEditMode && fields.disclaimer?.value"
        class="retailer-search-fi__disclaimer"
        v-html="fields.disclaimer.value"
      />
      <!-- eslint-enable vue/no-v-html -->

      <div
        v-if="loading"
        class="retailer-search-fi__retailers__loading"
      >
        <loading-indicator
          class="assisted-service-company-fi__c-spinner"
          variant="spinnerLocal"
        />
      </div>

      <div
        v-if="!noRetailersFound"
        class="retailer-search-fi__retailers"
      >
        <grid-columns-fi :columns="8">
          <retailer-search-result-fi
            v-if="googleMaps.retailers"
            class="fi-col-span-full"
            :retailers="googleMaps.retailers"
            :show-stock="haveArticles"
            :partner-type-logo="fields.partnerTypeLogo"
            :show-distance="haveLocation"
            :theme="theme"
            @clicked="activateResultItem"
          />
        </grid-columns-fi>
      </div>

      <div
        v-else
        class="fi-col-span-full"
      >
        <div class="retailer-search-fi__no-results">
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="isEditMode"
            class="retailer-search-fi__no-results__headline"
            v-html="fields.noResultsHeadline.editable"
          />
          <div
            v-else
            class="retailer-search-fi__no-results__headline"
            v-html="fields.noResultsHeadline.value"
          />
          <div
            v-if="isEditMode"
            class="retailer-search-fi__no-results__text"
            v-html="fields.noResultsText.editable"
          />
          <div
            v-else
            class="retailer-search-fi__no-results__text"
            v-html="fields.noResultsText.value"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
      </div>
    </template>
    <loading-indicator
      v-else
      variant="spinnerLocal"
    />
  </component-wrapper>
</template>

<script>
import { IconFi, LoadingIndicator, LoadingIndicatorWrapper } from 'atoms';
import axios from 'axios';
import CloudImageFi from 'components/atoms/cloud-image-fi/CloudImageFi';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper';
import GMapMap from 'components/google-maps/GMapMap';
import GoogleMapFetcherFi from 'components/google-maps/GoogleMapFetcherFi';
import GridColumnsFi from 'components/grid-fi/GridColumnsFi';
import BlockNotificationFi from 'components/notifications-fi/block-notification-fi/BlockNotificationFi';
import {
  getDirectionsMarkerIcon,
  getRetailerMarkerIcon,
} from 'components/retailer-search-fi/GetMarkerIconFi';
import googleMapsOptions from 'components/retailer-search-fi/GoogleMapsOptions';
import RetailerSearchFilterFi from 'components/retailer-search-fi/retailer-search-filter-fi/RetailerSearchFilterFi';
import RetailerDetailFi from 'components/retailer-search-fi/retailer-search-result-fi/RetailerDetailFi';
import RetailerSearchResultFi from 'components/retailer-search-fi/retailer-search-result-fi/RetailerSearchResultFi';
import GETRETAILERSEARCH from 'gql/getRetailerSearch.gql';
import breakpointMixin from 'mixins/breakpointMixin';
import distanceMixin from 'mixins/distanceMixin';
import eventHubMixin from 'mixins/eventHubMixin';
import { throttle } from 'throttle-debounce';
import { GMapCluster as GMapCluster, Marker as GMapMarker } from 'vue-google-maps-community-fork';

import getAssetUrl from '@/lib/AssetService';

const logDebug = (msg) => {
  if (!IS_SSR && window.debugMaps) {
    console.debug(msg);
  }
};

export default {
  name: 'RetailerSearchFi',
  components: {
    GMapMap,
    GoogleMapFetcherFi,

    GMapMarker,
    GMapCluster,

    RetailerSearchResultFi,
    CloudImageFi,
    ComponentWrapper,
    GridColumnsFi,
    RetailerSearchFilterFi,
    RetailerDetailFi,
    IconFi,
    BlockNotificationFi,
    LoadingIndicator,
    LoadingIndicatorWrapper,
  },
  mixins: [breakpointMixin, distanceMixin, eventHubMixin],
  inject: ['fields'],
  props: {
    productIds: {
      type: [String, Number, Array],
      default: null,
    },
    countryList: {
      type: Array,
      default: () => [],
    },
    defaultCountry: {
      type: String,
      default: null,
    },
    ranges: {
      type: Array,
      default: null,
    },
    defaultRange: {
      type: String,
      default: '10',
    },
    theme: {
      type: String,
      default: null,
    },
    retailerSearchPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      retailerName: '',
      location: null,
      locationError: false,
      userLocationError: false,
      showroomFallbackRetailer: null,
      range: null,
      noRetailersFound: false,
      country: null,
      countrySelector: null,
      currentLocation: null,
      directionsService: null,
      routeData: null,
      directions: {
        loading: false,
        active: false,
        origin: null,
        destination: null,
        markers: [],
        travelMode: 'DRIVING',
      },
      google: null,
      autocompleteService: null,
      googleMaps: {
        url: 'https://maps.googleapis.com/maps/api/geocode/json?latlng',
        lat: 0,
        lng: 0,
        newLat: 0,
        newLng: 0,
        defaultLat: 0,
        defaultLng: 0,
        defaultZoom: 6,
        zoom: 6,
        languageIsoCode: null,
        countries: null,
        autoCompleteOptions: {
          componentRestrictions: {
            country: null,
          },
        },
        googleMapsOptions,
        retailers: null,
        retailersCount: null,
        infoWindowOpened: false,
        closeIcon: getAssetUrl('/img/google-maps/066-cancel.svg'),
        markerRefArray: [],
        marker: {
          partnerType: false,
          Retailer: {},
          Distance: null,
          Unit: null,
          IsStocked: false,
        },
        facets: {
          facetsRetailerTypes: null,
          retailerTypes: [],
          availabilityStatus: '',
          partnerTypes: null,
        },
        clusterMarkerOptionStyles: [],
      },
      throttledGetRetailersByLocation: null,
      loading: false,
      activeRetailer: null,
      isMounted: false,
    };
  },
  computed: {
    renderer() {
      return {
        render: ({ count, position }) => {
          return new window.google.maps.Marker({
            label: {
              text: String(count),
              color: 'rgba(var(--color-white))',
              fontSize: '16px',
              fontFamily: '"fischerstrong semi bold", "Open Sans", sans-serif',
            },
            position,
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
            icon: {
              scaledSize: new window.google.maps.Size(51, 51),
              url: getAssetUrl('/img/google-maps-fi/marker-group.png'),
            },
          });
        },
      };
    },
    retailerRanges() {
      if (this.ranges) {
        return this.ranges;
      }

      // fallback
      return [
        { name: `10 ${this.unit}`, value: '10' },
        { name: `25 ${this.unit}`, value: '25' },
        { name: `50 ${this.unit}`, value: '50' },
        { name: `75 ${this.unit}`, value: '75' },
        { name: `100 ${this.unit}`, value: '100' },
      ];
    },
    retailerDefaultRange() {
      if (
        this.retailerRanges &&
        !this.retailerRanges.find((el) => el.value === this.defaultRange)
      ) {
        return this.retailerRanges[0].value;
      }

      return this.defaultRange;
    },
    haveLocation() {
      return !(
        (this.googleMaps.lat === 0 && this.googleMaps.lng === 0) ||
        (this.googleMaps.lat === this.googleMaps.defaultLat &&
          this.googleMaps.lng === this.googleMaps.defaultLng)
      );
    },
    haveArticles() {
      if (this.productIds) {
        if (typeof this.productIds === 'number' || this.productIds.length) {
          return true;
        }
      }
      return false;
    },
    retailerTypes() {
      if (this.googleMaps.facets.facetsRetailerTypes?.length) {
        const facets = [
          {
            name: this.$dict.get('dealer-Locator.Facets.Labels.Retailer-Types'),
            elements: [],
            multiselect: true,
          },
        ];

        this.googleMaps.facets.facetsRetailerTypes.forEach((facet) => {
          const subElements = [];
          if (facet.Facets?.length) {
            facet.Facets.forEach((subFacet) => {
              const isSelected = subFacet.Selected.toString() === 'true'; // API returns this value as String...

              subElements.push({
                name: subFacet.Name,
                type: 'partner',
                selected: isSelected,
                count: subFacet.RecordCount,
              });
            });
          }

          facets[0].elements.push({
            name: facet.Name,
            type: 'retailer',
            selected: facet.Selected,
            count: facet.RecordCount,
            subElements,
          });
        });

        return facets;
      }
      return [];
    },
    countries() {
      if (this.countryList?.length) {
        return this.countryList.map((country) => country.value);
      }
      return [];
    },
  },
  watch: {
    async country(country) {
      const newCountryLocation = this.countryLocations[country];
      if (!newCountryLocation) {
        // eslint-disable-next-line no-console
        console.log(
          `Current country '${country}' not found in list of locations`,
          this.countryLocations,
        );
        return;
      }

      const lat = parseFloat(newCountryLocation.lat);
      const lng = parseFloat(newCountryLocation.lng);
      const zoom = parseFloat(newCountryLocation.zoom) || 6;

      this.googleMaps.defaultLat = lat;
      this.googleMaps.defaultLng = lng;
      this.googleMaps.defaultZoom = zoom;

      const map = await this.getMapPromise();
      this.changeZoom(zoom);
      map.panTo({
        lat,
        lng,
      });
    },
  },
  async mounted() {
    this.range = this.defaultRange;
    const languageIsoCode = this.getLanguageIsoCode();
    this.googleMaps.languageIsoCode = languageIsoCode;
    const { countries } = this;
    this.googleMaps.autoCompleteOptions.componentRestrictions.country = [
      languageIsoCode,
      ...countries,
    ];

    if (this.isSM) {
      this.googleMaps.googleMapsOptions = {
        ...googleMapsOptions,
        zoomControl: true,
      };
    }

    if (IS_SHOWROOM) {
      const { data: retailerSearchDataJson } = await axios.get(
        '/__mocks__/data/retailerSearch.json',
      );

      this.showroomFallbackRetailer = retailerSearchDataJson;
    }

    this.throttledGetRetailersByLocation = throttle(400, this.getRetailersByLocation);
    this.getDefaultGoogleMapsWithCountry();
    this.isMounted = true;
  },
  methods: {
    getFacetsRetailerTypes(result, variables) {
      const retailerTypes = variables.retailerTypes || [];
      return result.GroupsRetailerType.map((group) => ({
        Name: group.Name,
        RecordCount: result.Retailers.filter((retailer) =>
          retailer.Retailer?.RetailerTypeList?.includes(group.Name),
        ).length,
        Selected: retailerTypes.includes(group.Name),
        Facets: group.SubGroup?.Elements
          ? group.SubGroup.Elements.map((subGroup) => ({
              Name: subGroup.Name,
              Selected: retailerTypes.includes(subGroup.Name),
              RecordCount: subGroup.RecordCount,
            }))
          : [],
      })).filter((group) => group.RecordCount);
    },
    getMapPromise() {
      return this.$refs.gmap.$mapPromise;
    },
    locationChanged(value) {
      this.location = value;
      this.locationError = false;

      if (value) {
        this.googleMaps.newLat = value.lat;
        this.googleMaps.newLng = value.lng;

        if (value.userloaction) {
          this.currentLocation = value;
        }
      } else {
        this.retailerName = '';
      }

      if (this.directions.active) {
        if (value) {
          this.directions.origin = value;
          this.updateRoute();
        }
      } else {
        this.sendGoogleMaps();
      }
    },
    retailerNameChanged(value) {
      this.retailerName = value;
      this.sendGoogleMaps();
    },
    setRange(value) {
      this.range = value;
      this.sendGoogleMaps();
    },
    userLocationErrorChanged(error) {
      this.userLocationError = Boolean(error);
    },
    getLanguageIsoCode() {
      if (this.defaultCountry) {
        return this.defaultCountry;
      }

      return this.$fischer.languageIsoCode();
    },
    activateResultItem(retailer) {
      this.clearNavigation();
      this.activateMarker(retailer);
      this.activeRetailer = retailer;

      const position = this.getMarkerPosition(retailer);

      this.changeZoom(16);
      const { gmap } = this.$refs;
      gmap.panTo(position);

      if (this.theme !== 'modal') {
        this.$smoothScroll({
          scrollTo: this.$refs.gmap.$el,
          duration: 500,
          updateHistory: false,
          offset: -150,
        });
      } else {
        this.$smoothScroll({
          scrollTo: this.$refs.gmap.$el,
          duration: 500,
          updateHistory: false,
          offset: -200,
          container: document.querySelector('.retail-search-dialog-fi .sweet-modal'),
        });
      }
    },
    isStocked(marker) {
      return Boolean(marker.AvailabilityStatus && marker.AvailabilityStatus === 'AVAILABLE');
    },
    activateMarker(activeRetailer) {
      this.clearNavigation();
      this.displayMarkerDetailInfo(activeRetailer);
      this.setMarkerIconActive(activeRetailer);
    },
    clearSomeData() {
      this.googleMaps.facets.facetsRetailerTypes = null;
      this.googleMaps.facets.partnerTypes = null;
      this.googleMaps.facets.retailerTypes = null;
      this.googleMaps.facets.availabilityStatus = null;
      this.googleMaps.retailers = null;
      this.googleMaps.retailersCount = null;
      this.googleMaps.infoWindowOpened = false;
      this.noRetailersFound = false;
      this.activeRetailer = null;
    },
    async getDefaultGoogleMapsWithCountry() {
      try {
        logDebug('getting countries');
        const { data } = await axios.get(getAssetUrl('/countries.json'));
        logDebug('got countries');
        this.countryLocations = data;
        this.country = this.googleMaps.languageIsoCode;
        if (
          this.countryList.length &&
          !this.countryList.some((country) => country.value === this.country)
        ) {
          console.error(
            `Current country '${this.country}' not in country-list`,
            this.countryLocations,
          );
        }
      } catch (e) {
        console.error('Error getting countries', e);
      }
    },
    changeZoom(event) {
      logDebug(`Zoom changed to ${event}`);
      this.googleMaps.zoom = event;
    },
    async sendGoogleMaps() {
      if ((this.retailerName && this.retailerName !== '') || this.location) {
        this.clearSomeData();
        this.searchActive = true;

        // wait for animation
        await this.$nextTick();

        // Check if address is set
        if (this.googleMaps.newLat) {
          this.googleMaps.lat = this.googleMaps.newLat;
          this.googleMaps.lng = this.googleMaps.newLng;

          const circle = new window.google.maps.Circle({
            zoom: this.googleMaps.zoom,
            radius: this.lengthUnitDistance(this.range),
            center: { lat: parseFloat(this.googleMaps.lat), lng: parseFloat(this.googleMaps.lng) },
          });

          this.$refs.gmap.fitBounds(circle.getBounds());
        } else {
          this.googleMaps.lat = this.googleMaps.defaultLat;
          this.googleMaps.lng = this.googleMaps.defaultLng;

          this.changeZoom(this.googleMaps.defaultZoom);
        }

        this.throttledGetRetailersByLocation();
      } else {
        this.resetGoogleMaps();
      }
    },
    getRetailersByLocation(doNotUpdateFacets) {
      this.loading = true;
      const variables = {
        brand: this.$fischer.brandId(),
        language: this.$fischer.catalogLocale(),
        latitude: this.googleMaps.lat === 0 ? '.000000' : this.googleMaps.lat.toString(),
        longitude: this.googleMaps.lng === 0 ? '.000000' : this.googleMaps.lng.toString(),
        distance: this.haveLocation ? this.range.toString() : '50000',
        unit: this.$fischer.lengthUnit(),
        retailerTypes: this.googleMaps.facets.retailerTypes,
        availabilityStatus: this.productIds ? 'AVAILABLE' : undefined,
        partnerTypes: this.googleMaps.facets.partnerTypes,
        query: this.retailerName,
        articleNumbers: this.productIds,
        campaignRetailer: Boolean(this.fields?.campaignRetailers?.value),
        hiddenRetailers: Boolean(this.fields?.hiddenRetailers?.value),
      };

      const items = variables.articleNumbers
        ? variables.articleNumbers.split(',').map((item) => ({ productId: item }))
        : undefined;
      const eventType = items ? 'retail_connect_local' : 'retailer_search';
      this.sendEvent(eventType, {
        searchTerm: this.location?.description,
        items,
        retailerQuery: variables.query,
        latitude: variables.latitude,
        longitude: variables.longitude,
        distance: variables.distance,
        unit: variables.unit,
        retailerTypes: variables.retailerTypes,
        availabilityStatus: variables.availabilityStatus,
        partnerTypes: variables.partnerTypes,
        campaignRetailer: variables.campaignRetailers,
        hiddenRetailers: variables.hiddenRetailers,
      });

      this.$apollo
        .query({
          query: GETRETAILERSEARCH,
          variables,
        })
        .then((response) => {
          let retailerSearchResponse = response.data?.getRetailerSearch;

          if (retailerSearchResponse) {
            const googleMaps = { ...this.googleMaps };
            if (!doNotUpdateFacets && retailerSearchResponse.GroupsRetailerType) {
              googleMaps.facets.facetsRetailerTypes = this.getFacetsRetailerTypes(
                retailerSearchResponse,
                variables,
              );
            }
            googleMaps.retailers = retailerSearchResponse.Retailers.map((retailer) => ({
              ...retailer,
              isStocked: this.isStocked(retailer),
            }));
            googleMaps.retailersCount = retailerSearchResponse.RecordCount;
            this.googleMaps = googleMaps;
          }

          this.noRetailersFound = !(this.googleMaps.retailersCount > 0);
          this.loading = false;
        })
        .catch((err) => {
          this.noRetailersFound = true;
          this.loading = false;
          this.error = err;
        });
    },
    getMarkerPosition(marker) {
      return {
        lat: parseFloat(marker.Retailer.Address.Latitude),
        lng: parseFloat(marker.Retailer.Address.Longitude),
      };
    },
    getMarkerIcon(marker, retailer) {
      if (retailer) {
        return getRetailerMarkerIcon(marker.isActive, this.haveArticles, this.isStocked(marker));
      }
      return getDirectionsMarkerIcon(marker);
    },
    closeInfoWindow() {
      this.googleMaps.infoWindowOpened = false;
      this.setMarkerIconActive(null);

      if (this.activeRetailer) {
        this.activeRetailer = null;
        this.sendGoogleMaps();
      }
    },
    setMarkerIconActive(activeRetailer) {
      this.googleMaps.retailers.forEach((retailer) => {
        retailer.isActive = false;
      });

      if (activeRetailer) {
        activeRetailer.isActive = true;
      }
    },
    displayMarkerDetailInfo(marker) {
      this.googleMaps.infoWindowOpened = true;

      this.googleMaps.marker.Retailer = marker.Retailer;
      this.googleMaps.marker.Distance = marker.Distance;
      this.googleMaps.marker.Unit = marker.Unit;
      this.googleMaps.marker.IsStocked = this.isStocked(marker);

      if (marker.Retailer.PartnerType) {
        this.googleMaps.marker.partnerType = true;
      } else {
        this.googleMaps.marker.partnerType = false;
      }
    },
    retailerTypeChanged(types) {
      if (!types) {
        this.googleMaps.facets.retailerTypes = null;
        this.googleMaps.facets.partnerTypes = null;
        return;
      }

      const { partners, retailers } = types;
      if (retailers.length) {
        this.googleMaps.facets.retailerTypes = retailers.map((el) => el.name);
        const circle = new window.google.maps.Circle({
          zoom: this.googleMaps.zoom,
          radius: this.lengthUnitDistance(this.range),
          center: {
            lat: parseFloat(this.googleMaps.newLat),
            lng: parseFloat(this.googleMaps.newLng),
          },
        });

        this.$refs.gmap.fitBounds(circle.getBounds());
      } else {
        this.googleMaps.facets.retailerTypes = null;
      }

      if (partners.length) {
        this.googleMaps.facets.partnerTypes = partners.map((el) => el.name);
      } else {
        this.googleMaps.facets.partnerTypes = null;
      }

      this.closeInfoWindow();
      this.getRetailersByLocation(true);
    },
    async resetGoogleMaps() {
      this.clearSomeData();
      this.clearNavigation();
      this.retailerName = '';
      this.googleMaps.lat = 0;
      this.googleMaps.lng = 0;
      this.googleMaps.newLat = 0;
      this.googleMaps.newLng = 0;
      this.googleMaps.lat = this.googleMaps.defaultLat;
      this.googleMaps.lng = this.googleMaps.defaultLng;
      this.googleMaps.facets.retailerTypes = null;
      this.changeZoom(this.googleMaps.defaultZoom);
      const map = await this.getMapPromise();
      map.panTo({
        lat: this.googleMaps.lat,
        lng: this.googleMaps.lng,
      });
      map.zoom = this.googleMaps.zoom;
    },
    navigateTo(data) {
      if (this.directions.loading) {
        return;
      }

      this.clearNavigation();

      this.directions.active = true;
      this.directions.destination = data;

      if (!this.location) {
        this.locationError = true;
      } else {
        this.directions.origin = this.location;
        this.setRoute();
      }
    },
    updateRoute() {
      this.directions.markers = [];
      if (this.directionsDisplay) {
        this.directionsDisplay.setDirections({ routes: [] });
      }
      this.setRoute();
    },
    setRoute() {
      const map = this.$refs.gmap.$mapObject;
      if (!this.directionsDisplay) {
        this.directionsDisplay = new window.google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
        });
      }

      this.directionsService = new window.google.maps.DirectionsService();
      const { origin, destination, markers, travelMode } = this.directions;

      this.directionsService.route(
        {
          origin,
          destination,
          travelMode,
        },
        (response, directionsStatus) => {
          if (directionsStatus !== 'OK') return;
          this.directionsDisplay.setDirections(response);
          const route = response.routes[0].legs[0];
          this.routeData = route;

          markers.push(
            {
              name: 'Start',
              position: route.start_location,
              draggable: true,
              icon: 'Start',
            },
            {
              name: 'End',
              position: route.end_location,
              draggable: false,
              icon: 'End',
            },
          );
        },
      );
    },
    closeDirections() {
      this.retailerName = '';
      this.setMarkerIconActive(null);
      this.clearNavigation();
      this.locationChanged(null);
    },
    clearNavigation() {
      if (!this.directions.active) {
        return;
      }

      if (this.directionsDisplay) {
        this.directionsDisplay.setDirections({ routes: [] });
      }

      this.directions.loading = false;
      this.directions.active = false;
      this.directions.origin = null;
      this.directions.destination = null;
      this.directions.markers = [];
    },
    markerDragged(data) {
      this.directions.origin.lat = data.latLng.lat();
      this.directions.origin.lng = data.latLng.lng();
      this.updateRoute();
    },
    openGoogleMaps() {
      if (IS_SSR) {
        return;
      }

      const { origin, destination, travelMode } = this.directions;
      const params = `origin=${origin.description}&destination=${
        destination.description
      }&travelmode=${travelMode.toLowerCase()}`;
      const platform = navigator?.userAgentData?.platform;
      if (platform && ['iPhone', 'iPod', 'iPad'].some((el) => platform.includes(el))) {
        window.open(`maps://www.google.com/maps/dir/?api=1&${params}`);
      } else {
        window.open(`https://www.google.com/maps/dir/?api=1&${params}`);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import './retailer-search-fi';
</style>

<style lang="scss">
@import './retailer-search-fi-unscoped';
</style>
